import { useEffect, useState } from 'react';
import { LLMOutputComponent, useLLMOutput, BlockMatch } from '@llm-ui/react';
import { markdownLookBack } from '@llm-ui/markdown';
import { codeBlockLookBack } from '@llm-ui/code';
import {
  fullCodeBlockMatcher,
  partialCodeBlockMatcher,
} from '@/lib/markdown-matchers';
import { normalizeMarkdownCodeBlock } from '@/lib/markdown-code-utils';

type UseStreamingTextProps = {
  output: string;
  MarkdownComponent: LLMOutputComponent;
  CodeBlock: LLMOutputComponent;
  isGenerating?: boolean;
  isStreamFinished?: boolean;
};

type UseStreamingTextReturn = {
  codeBlocks: BlockMatch[];
  isStreamFinished: boolean;
  setIsStreamFinished: (value: boolean) => void;
  hasContent: boolean;
};

export default function useStreamingText({
  output,
  MarkdownComponent,
  CodeBlock,
  isGenerating = false,
  isStreamFinished: externalIsStreamFinished = false,
}: UseStreamingTextProps): UseStreamingTextReturn {
  const [isStreamFinished, setIsStreamFinished] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  const [processedOutput, setProcessedOutput] = useState('');

  useEffect(() => {
    const processed = normalizeMarkdownCodeBlock(output);
    setProcessedOutput(processed);

    // Reset stream finished state when generating starts
    if (isGenerating) {
      setIsStreamFinished(false);
    } else {
      // Use external isStreamFinished if provided, otherwise fall back to local logic
      if (externalIsStreamFinished) {
        setIsStreamFinished(true);
      } else if (processed && !isGenerating) {
        setIsStreamFinished(true);
      }
    }

    if (processed.trim() !== '') {
      setHasContent(true);
    } else {
      setHasContent(false);
    }
  }, [output, isGenerating, externalIsStreamFinished]);

  const { blockMatches } = useLLMOutput({
    llmOutput: processedOutput,
    fallbackBlock: {
      component: MarkdownComponent,
      lookBack: markdownLookBack(),
    },
    blocks: [
      {
        component: CodeBlock,
        findCompleteMatch: fullCodeBlockMatcher(),
        findPartialMatch: partialCodeBlockMatcher(),
        lookBack: codeBlockLookBack(),
      },
    ],
    isStreamFinished,
  });

  return {
    codeBlocks: blockMatches,
    isStreamFinished,
    setIsStreamFinished,
    hasContent,
  };
}
